import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { organizationsAPI, contactsAPI, venuesAPI, notesAPI, activitiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Building2,
  Pencil,
  Trash2,
  Users,
  MapPin,
  FileText,
  History,
  ArrowLeft,
  Plus,
  Send,
  Calendar,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'client':
      return 'badge-client';
    case 'venue_owner':
      return 'badge-venue';
    case 'vendor':
      return 'badge-vendor';
    case 'partner':
      return 'badge-partner';
    default:
      return '';
  }
};

const OrganizationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [orgRes, contactsRes, venuesRes, notesRes, activitiesRes, eventsRes] = await Promise.all([
        organizationsAPI.get(id),
        contactsAPI.list({ organization_id: id, limit: 1000 }),
        venuesAPI.list({ organization_id: id, limit: 1000 }),
        notesAPI.list('organization', id),
        activitiesAPI.list('organization', id),
        organizationsAPI.getEvents(id),
      ]);
      setOrganization(orgRes.data);
      // Handle paginated response format
      setContacts(contactsRes.data.data || contactsRes.data);
      setVenues(venuesRes.data.data || venuesRes.data);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
      setLinkedEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load organization');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await notesAPI.create({
        entity_type: 'organization',
        entity_id: id,
        content: newNote,
      });
      setNewNote('');
      loadData();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDelete = async () => {
    try {
      await organizationsAPI.delete(id);
      toast.success('Organization deleted');
      navigate('/organizations');
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!organization) return null;

  return (
    <div className="space-y-6" data-testid="organization-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/organizations">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary" />
              {organization.organization_name}
            </h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {organization.org_roles.map((role) => (
                <Badge
                  key={role}
                  variant="outline"
                  className={`${getRoleBadgeClass(role)}`}
                >
                  {role.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 ml-12 sm:ml-0">
          <Button variant="outline" asChild data-testid="edit-org-btn">
            <Link to={`/organizations/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowDelete(true)}
              data-testid="delete-org-btn"
            >
              <Trash2 className="h-4 w-4 mr-2 text-destructive" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="contacts">
                Contacts ({contacts.length})
              </TabsTrigger>
              <TabsTrigger value="venues">
                Venues ({venues.length})
              </TabsTrigger>
              <TabsTrigger value="events">
                Events ({linkedEvents.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-6">
              {/* Billing Info */}
              {(organization.billing_terms || organization.billing_notes) && (
                <Card className="border-border/50 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Billing Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {organization.billing_terms && (
                      <div>
                        <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                          Terms
                        </p>
                        <p>{organization.billing_terms}</p>
                      </div>
                    )}
                    {organization.billing_notes && (
                      <div>
                        <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                          Notes
                        </p>
                        <p className="whitespace-pre-wrap">{organization.billing_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Client Intelligence */}
              {organization.client_intelligence && (
                <Card className="border-border/50 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">
                      Client / Corporate Intelligence
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{organization.client_intelligence}</p>
                  </CardContent>
                </Card>
              )}

              {/* Lessons Learned */}
              {organization.lessons_learned && (
                <Card className="border-border/50 bg-card/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Lessons Learned</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{organization.lessons_learned}</p>
                  </CardContent>
                </Card>
              )}

              {/* Account Manager */}
              {organization.account_manager_id && organization.account_manager_name && (
                <Card className="border-border/50 bg-card/50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                      Account Manager
                    </p>
                    <Link 
                      to={`/contacts/${organization.account_manager_id}`}
                      className="font-medium text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                    >
                      {organization.account_manager_name}
                    </Link>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="contacts" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-cyan-400" />
                    Contacts
                  </CardTitle>
                  <Button size="sm" asChild>
                    <Link to={`/contacts/new?organization_id=${id}`}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  {contacts.length > 0 ? (
                    <div className="space-y-3">
                      {contacts.map((contact) => (
                        <Link
                          key={contact.id}
                          to={`/contacts/${contact.id}`}
                          className="block p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                        >
                          <p className="font-medium">{contact.contact_name}</p>
                          {contact.title && (
                            <p className="text-sm text-muted-foreground">{contact.title}</p>
                          )}
                          {contact.email && (
                            <p className="text-sm text-muted-foreground">{contact.email}</p>
                          )}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-6">
                      No contacts linked to this organization
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="venues" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-amber-400" />
                    Venues
                  </CardTitle>
                  <Button size="sm" asChild>
                    <Link to={`/venues/new?organization_id=${id}`}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  {venues.length > 0 ? (
                    <div className="space-y-3">
                      {venues.map((venue) => (
                        <Link
                          key={venue.id}
                          to={`/locations/${venue.id}`}
                          className="block p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                        >
                          <p className="font-medium">{venue.location_name}</p>
                          {venue.street_address && (
                            <p className="text-sm text-muted-foreground">
                              {venue.street_address}
                            </p>
                          )}
                          {(venue.city || venue.state) && (
                            <p className="text-sm text-muted-foreground">
                              {[venue.city, venue.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-6">
                      No venues linked to this organization
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="events" className="mt-6">
              <Card className="border-border/50 bg-card/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-emerald-400" />
                    Linked Events
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {linkedEvents.length > 0 ? (
                    <Table data-testid="org-linked-events-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event Name</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Location</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {linkedEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <Link
                                to={`/events/${event.id}`}
                                className="font-medium hover:text-primary transition-colors"
                                data-testid={`org-event-link-${event.id}`}
                              >
                                {event.event_name}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {event.start_date
                                ? format(new Date(event.start_date), 'MMM d, yyyy')
                                : '-'}
                              {event.end_date && event.end_date !== event.start_date
                                ? ` - ${format(new Date(event.end_date), 'MMM d, yyyy')}`
                                : ''}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  event.status === 'confirmed'
                                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                    : event.status === 'tentative'
                                    ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400'
                                    : event.status === 'completed'
                                    ? 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400'
                                    : event.status === 'cancelled'
                                    ? 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400'
                                    : 'border-red-500/30 bg-red-500/10 text-red-400'
                                }
                              >
                                {event.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {event.location_name || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-muted-foreground text-center py-6">
                      No events linked to this organization
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Column - Notes & Activity */}
        <div className="space-y-6">
          {/* Add Note */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Add Note
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Write a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="bg-background/50 min-h-[100px]"
                data-testid="note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="w-full"
                data-testid="add-note-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </CardContent>
          </Card>

          {/* Notes */}
          {notes.length > 0 && (
            <Card className="border-border/50 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px] pr-4">
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className="p-3 rounded-lg bg-background/50">
                        <p className="whitespace-pre-wrap text-sm">{note.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {note.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Activity History */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <History className="h-5 w-5 text-cyan-400" />
                History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px] pr-4">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div key={activity.id} className="text-sm">
                        <p>
                          <span className="font-medium">{activity.action}</span>
                          {' - '}
                          {activity.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </p>
                        <Separator className="mt-3" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No history yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{organization.organization_name}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrganizationDetail;
